import React, { FC } from 'react'
import styled from 'styled-components'

import { Button } from '@ucheba/ui/components/Button/desktop'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'

import { getApiImageUrl } from '@ucheba/utils/helpers/string'
import GraduationCapIcon from '@ucheba/ui/icons/icon__graduation-cap.svg'
import GraduationCapCheckIcon from '@ucheba/ui/icons/icon__graduation-cap-check.svg'
import IconStarFilled from '@ucheba/ui/icons/icon__star-filled.svg'

import { Chips } from '@ucheba/ui/components/Chips/desktop'
import { IProgram } from '@ucheba/store/institutions/programs/types'
import { IProgramItemProps } from '../../types'
import { HEAD_COLORS, PROBABILITY_TYPES } from '../../constants'

import {
  Raiting,
  TextStyled,
  Subject,
  Block,
  ChipsStyled,
  ListItemStyled,
  Divider,
} from '../../styles'
import { useProgramItem } from '../bll/useProgramItem'

const ProgramFaculty = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
`

const Stats = styled.div`
  display: flex;
  column-gap: 8px;
  flex-wrap: wrap;
`

const ChipsBlock = styled.div`
  height: 32px;
  display: flex;
  column-gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`

const Subjects = styled(Stats)``

interface ISubjectsBlockProp {
  title: string
  list: IProgram['optionalSubjects']
}

const SubjectsBlock: FC<ISubjectsBlockProp> = ({ title, list }) => (
  <Subjects>
    <TextStyled fontSize='h6' lineHeight='h6' color='black' fontWeight='semibold'>
      {title}
    </TextStyled>

    {list?.map((subjects) => (
      <Block key={subjects.id}>
        {subjects?.iconUrl && (
          <img
            width='16'
            height='16'
            src={getApiImageUrl(subjects.iconUrl)}
            alt={subjects.name}
          />
        )}

        <Subject>{subjects.name}</Subject>
      </Block>
    ))}
  </Subjects>
)

const ProgramItem: FC<IProgramItemProps> = (props) => {
  const {
    id,
    institution,
    name,
    programType,
    feedbackStats,
    averageBySubjectExamScoreFrom,
    budgetPlacesCount,
    priceFrom,
    requiredSubjects,
    optionalSubjects,
    probability,
  } = props

  const {
    isProgramInPlan,
    probabilityForProgram,
    handleAddProgram,
    handleDeleteProgram,
  } = useProgramItem(id, probability)

  return (
    <ListItemStyled>
      <ProgramFaculty>
        {institution?.abbreviation && (
          <Text
            fontSize='h5'
            lineHeight='h5'
            fontWeight='medium'
            color='blue'
            html={false}
          >
            <a href={`/uz/${institution.id}`} target='_blank' rel='noreferrer'>
              {institution?.abbreviation}
            </a>
          </Text>
        )}

        <ChipsBlock>
          <Chips firstLetterUppercase color='gray5'>
            {programType.name}
          </Chips>

          {typeof probabilityForProgram === 'string' && (
            <ChipsStyled
              firstLetterUppercase
              textColor={HEAD_COLORS[probabilityForProgram].heading}
              color={HEAD_COLORS[probabilityForProgram].box}
            >
              {PROBABILITY_TYPES[probabilityForProgram]} шансы
            </ChipsStyled>
          )}
        </ChipsBlock>
      </ProgramFaculty>
      <Spacing spacing='xxsmall' />
      <Text html={false} fontSize='h3' lineHeight='h3' fontWeight='bold' color='blue'>
        <a href={`/program/${id}`} target='_blank' rel='noreferrer'>
          {name}
        </a>
      </Text>
      <Spacing spacing='xxsmall' />
      <Stats>
        {!!feedbackStats?.score && (
          <Raiting>
            <IconStarFilled />

            <Text fontSize='h5' lineHeight='h5' fontWeight='semibold' color='orange'>
              {feedbackStats.score}
            </Text>
          </Raiting>
        )}

        {!!averageBySubjectExamScoreFrom && (
          <Block>
            <Text fontSize='h5' lineHeight='h5' color='black' fontWeight='semibold'>
              {Math.round(averageBySubjectExamScoreFrom)}
            </Text>

            <Text fontSize='h5' lineHeight='h5' fontWeight='regular' color='gray60'>
              проходной балл
            </Text>
          </Block>
        )}

        {!!budgetPlacesCount && (
          <Block>
            <Text fontSize='h5' lineHeight='h5' color='black' fontWeight='semibold'>
              {budgetPlacesCount}
            </Text>

            <Text fontSize='h5' lineHeight='h5' fontWeight='regular' color='gray60'>
              бюджетных мест
            </Text>
          </Block>
        )}

        {priceFrom && (
          <Block>
            <Text fontSize='h5' lineHeight='h5' color='black' fontWeight='semibold'>
              {`от ${priceFrom}`}
            </Text>

            <Text fontSize='h5' lineHeight='h5' fontWeight='regular' color='gray60'>
              за год
            </Text>
          </Block>
        )}
      </Stats>
      <Spacing spacing='small' />
      <SubjectsBlock title='Обязательно:' list={requiredSubjects} />
      <Spacing spacing='xsmall' />
      {!!optionalSubjects?.length && (
        <SubjectsBlock title='На выбор:' list={optionalSubjects} />
      )}
      <Spacing spacing='small' />

      <Button
        view='outline'
        onClick={isProgramInPlan ? handleDeleteProgram : handleAddProgram}
        StartIcon={isProgramInPlan ? <GraduationCapCheckIcon /> : <GraduationCapIcon />}
        color='blue'
      >
        {isProgramInPlan ? 'В плане' : 'Хочу поступить'}
      </Button>

      <Divider />
    </ListItemStyled>
  )
}

export default ProgramItem
